import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Breadcrumbs } from "../AdminLayout";
import { adminApi, formatErr } from "../api";
import { toast, Toaster } from "sonner";
import { Lock, ExternalLink } from "lucide-react";

const privateInsightsApi = {
  list: (params = {}) => adminApi.get("/admin/clinical-insights/private", { params }).then(r => r.data),
};

const HOST_TO_ROUTE = {
  articles: "articles", conditions: "conditions", guidelines: "guidelines",
  research_updates: "research-updates", evidence_summaries: "evidence-summaries"
};

const VARIANT_LABEL = {
  clinical_pearl: "Clinical Pearl",
  prevention_note: "Prevention Note",
  pitfall_to_avoid: "Pitfall To Avoid",
  when_to_refer: "When To Refer",
  decision_point: "Decision Point",
  patient_conversation: "Patient Conversation",
};

export default function InsightsPrivate() {
  const [rows, setRows] = useState([]);
  const [variant, setVariant] = useState("");
  const [hostType, setHostType] = useState("");

  const reload = () => {
    privateInsightsApi.list({
      variant: variant || undefined,
      host_type: hostType || undefined,
    }).then(setRows).catch(e => toast.error(formatErr(e.response?.data?.detail)));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [variant, hostType]);

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="insights-private">
      <Toaster position="top-right" richColors/>
      <Breadcrumbs items={[{ label: "Clinical Insights" }, { label: "Private · Admin" }]}/>
      <div className="mt-4 mb-2 flex items-center gap-3">
        <Lock size={20} className="text-prestige"/>
        <h1 className="font-serif text-3xl text-navy tracking-tight">Insights · Private Admin</h1>
      </div>
      <p className="text-sm text-stone2 mb-6 max-w-3xl">
        These insights are never surfaced on the public site or the doctor portal. They stay inside the admin CMS as internal editorial notes, contested drafts, or context reserved for the clinical team.
      </p>

      <div className="flex flex-wrap gap-3 mb-4">
        <select data-testid="ip-host" value={hostType} onChange={e => setHostType(e.target.value)}
                className="border border-line px-3 py-2 text-sm">
          <option value="">All hosts</option>
          {Object.keys(HOST_TO_ROUTE).map(k => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
        </select>
        <select data-testid="ip-variant" value={variant} onChange={e => setVariant(e.target.value)}
                className="border border-line px-3 py-2 text-sm">
          <option value="">All variants</option>
          {Object.keys(VARIANT_LABEL).map(k => <option key={k} value={k}>{VARIANT_LABEL[k]}</option>)}
        </select>
        <div className="ml-auto mono text-[10px] uppercase tracking-widest text-stone2 self-center">{rows.length} insight{rows.length === 1 ? "" : "s"}</div>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white border border-line p-10 text-center text-sm text-stone2">
          No private_admin insights match the current filters.
        </div>
      ) : (
        <div className="bg-white border border-line divide-y divide-line">
          {rows.map((r, i) => {
            const route = HOST_TO_ROUTE[r.host_type];
            return (
              <div key={`${r.host_id}-${r.insight.id}-${i}`} data-testid={`ip-${r.insight.id}`} className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="mono text-[10px] uppercase tracking-widest text-stone2">{r.host_type.replace(/_/g, " ")} · {r.host_title}</div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <div className="font-serif text-xl text-navy">{r.insight.title}</div>
                      <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-prestige/10 text-prestige">
                        {VARIANT_LABEL[r.insight.variant] || r.insight.variant}
                      </span>
                      <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-[#9F1239]/10 text-[#9F1239]">Private Admin</span>
                    </div>
                    <div className="prose-article text-sm mt-3" dangerouslySetInnerHTML={{ __html: (r.insight.body_markdown || "").replace(/\n/g, "<br>") }}/>
                  </div>
                  {route && (
                    <Link to={`/admin/content/${route}/${r.host_id}`} data-testid={`ip-open-${r.insight.id}`}
                          className="mono text-[10px] uppercase tracking-widest text-navy hover:text-terracotta inline-flex items-center gap-1 shrink-0">
                      Open host <ExternalLink size={11}/>
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
